import React, { useState, useEffect } from 'react'
import { Send, Users, AlertCircle, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { getMembersForSMS, sendBulkSMS, getSMSTemplates, renderTemplate, SMSTemplate } from '../../services/communicationsService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useSubscription, useCanSendSMS } from '@/hooks/useSubscription'
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt'

interface SMSCampaignProps {
  churchId: string
}

const SMSCampaign: React.FC<SMSCampaignProps> = ({ churchId }) => {
  const [step, setStep] = useState<'compose' | 'recipients' | 'review' | 'sending' | 'complete'>('compose')
  const [message, setMessage] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<SMSTemplate | null>(null)
  const [templateVars, setTemplateVars] = useState<Record<string, string>>({})

  const [recipients, setRecipients] = useState<any[]>([])
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set())
  const [roleFilter, setRoleFilter] = useState<string[]>([])

  const [loading, setLoading] = useState(false)
  const [sendResult, setSendResult] = useState<{
    successful: number
    failed: number
    errors: string[]
  } | null>(null)

  const { limits, usage } = useSubscription(churchId)
  const recipientCount = selectedRecipients.size
  const { canSend, remaining } = useCanSendSMS(churchId, recipientCount)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)

  const templates = getSMSTemplates()
  const characterCount = message.length
  const smsCount = Math.ceil(characterCount / 160)

  useEffect(() => {
    loadRecipients()
  }, [churchId, roleFilter])

  const loadRecipients = async () => {
    try {
      setLoading(true)
      const data = await getMembersForSMS(churchId, {
        roles: roleFilter.length > 0 ? roleFilter : undefined
      })
      setRecipients(data || [])
    } catch (error) {
      console.error('Failed to load recipients:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTemplateSelect = (template: SMSTemplate) => {
    setSelectedTemplate(template)
    setTemplateVars({})
  }

  const handleTemplateVarChange = (variable: string, value: string) => {
    setTemplateVars(prev => ({ ...prev, [variable]: value }))
  }

  const applyTemplate = () => {
    if (selectedTemplate) {
      const renderedMessage = renderTemplate(selectedTemplate, templateVars)
      setMessage(renderedMessage)
      setSelectedTemplate(null)
      setStep('compose')
    }
  }

  const toggleRecipient = (id: string) => {
    setSelectedRecipients(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const selectAll = () => {
    setSelectedRecipients(new Set(recipients.map(r => r.id)))
  }

  const clearAll = () => {
    setSelectedRecipients(new Set())
  }

  const toggleRole = (role: string) => {
    setRoleFilter(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    )
  }

  const handleSend = async () => {
    if (!canSend) {
      setShowUpgradePrompt(true)
      return
    }

    setStep('sending')

    const recipientPhones = recipients
      .filter(r => selectedRecipients.has(r.id))
      .map(r => r.phone)

    try {
      const result = await sendBulkSMS(recipientPhones, message)
      setSendResult(result)
      setStep('complete')
    } catch (error: any) {
      console.error('Send failed:', error)
      setSendResult({
        successful: 0,
        failed: recipientPhones.length,
        errors: [error.message]
      })
      setStep('complete')
    }
  }

  const reset = () => {
    setStep('compose')
    setMessage('')
    setSelectedRecipients(new Set())
    setSendResult(null)
    setSelectedTemplate(null)
    setTemplateVars({})
  }

  // Step 1: Compose Message
  if (step === 'compose') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Compose SMS</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Template Selection */}
          <div className="space-y-2">
            <Label>Use Template (Optional)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {templates.map(template => (
                <Button
                  key={template.id}
                  variant="outline"
                  onClick={() => handleTemplateSelect(template)}
                  className="h-auto p-3 justify-start"
                >
                  <div className="text-left">
                    <div className="font-medium text-sm">{template.name}</div>
                    <div className="text-xs text-muted-foreground mt-1 capitalize">{template.category.replace(/_/g, ' ')}</div>
                  </div>
                </Button>
              ))}
            </div>
          </div>

          {/* Template Variable Input */}
          {selectedTemplate && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Fill Template Variables</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {selectedTemplate.variables.map(variable => (
                  <div key={variable} className="space-y-2">
                    <Label htmlFor={variable} className="capitalize">
                      {variable.replace(/_/g, ' ')}
                    </Label>
                    <Input
                      id={variable}
                      type="text"
                      value={templateVars[variable] || ''}
                      onChange={(e) => handleTemplateVarChange(variable, e.target.value)}
                      placeholder={`Enter ${variable}`}
                    />
                  </div>
                ))}
                <Button onClick={applyTemplate} className="w-full">
                  Apply Template
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Message Input */}
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder="Type your message here..."
            />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {characterCount} characters • {smsCount} SMS
              </span>
              <span className="text-muted-foreground">
                Cost: ~GHS {(smsCount * 0.035).toFixed(3)} per recipient
              </span>
            </div>
          </div>

          <Alert className={remaining < 10 ? 'border-yellow-500' : ''}>
            <AlertDescription>
              {remaining} SMS remaining this month out of {limits?.max_sms_monthly || 0}
              {remaining < 10 && ' - Running low!'}
            </AlertDescription>
          </Alert>

          <Button
            onClick={() => setStep('recipients')}
            disabled={!message.trim()}
            className="w-full"
          >
            Next: Select Recipients
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Step 2: Select Recipients
  if (step === 'recipients') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select Recipients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Role Filter */}
          <div className="space-y-2">
            <Label>Filter by Role</Label>
            <div className="flex flex-wrap gap-2">
              {['pastor', 'elder', 'deacon', 'member'].map(role => (
                <Button
                  key={role}
                  variant={roleFilter.includes(role) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleRole(role)}
                  className="capitalize"
                >
                  {role}
                </Button>
              ))}
            </div>
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearAll}>
                Clear All
              </Button>
            </div>
            <Badge variant="secondary">
              {selectedRecipients.size} of {recipients.length} selected
            </Badge>
          </div>

          {/* Recipients List */}
          <div className="max-h-96 overflow-y-auto space-y-2 border rounded-md p-2">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading recipients...</div>
            ) : recipients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No members with phone numbers found
              </div>
            ) : (
              recipients.map(recipient => (
                <label
                  key={recipient.id}
                  className="flex items-center gap-3 p-3 rounded-md border bg-card hover:bg-accent cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={selectedRecipients.has(recipient.id)}
                    onCheckedChange={() => toggleRecipient(recipient.id)}
                  />
                  <div className="flex-1">
                    <div className="font-medium">
                      {recipient.first_name} {recipient.last_name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {recipient.phone} • {recipient.role}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setStep('compose')}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={() => setStep('review')}
              disabled={selectedRecipients.size === 0}
              className="flex-1"
            >
              Review & Send
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Step 3: Review
  if (step === 'review') {
    const totalCost = selectedRecipients.size * smsCount * 0.035

    return (
      <Card>
        <CardHeader>
          <CardTitle>Review Campaign</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Message Preview */}
          <div className="space-y-2">
            <Label>Message</Label>
            <div className="p-4 rounded-md border bg-muted whitespace-pre-wrap">
              {message}
            </div>
            <p className="text-sm text-muted-foreground">
              {characterCount} characters • {smsCount} SMS per recipient
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground mb-1">Recipients</div>
                <div className="text-2xl font-bold">{selectedRecipients.size}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground mb-1">Est. Cost</div>
                <div className="text-2xl font-bold">GHS {totalCost.toFixed(2)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Warning */}
          <Alert>
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              You are about to send {selectedRecipients.size * smsCount} SMS messages.
              This action cannot be undone. Please review carefully before sending.
            </AlertDescription>
          </Alert>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setStep('recipients')}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={handleSend}
              className="flex-1"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Campaign
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Step 4: Sending
  if (step === 'sending') {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4 text-primary" />
          <h3 className="text-xl font-semibold mb-2">Sending Messages...</h3>
          <p className="text-muted-foreground">This may take a few moments</p>
        </CardContent>
      </Card>
    )
  }

  // Step 5: Complete
  if (step === 'complete' && sendResult) {
    const successRate = sendResult.successful / (sendResult.successful + sendResult.failed) * 100

    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center mb-6">
            {sendResult.failed === 0 ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Campaign Sent Successfully!</h3>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Campaign Completed</h3>
              </>
            )}
          </div>

          {/* Results */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="text-center pt-6">
                <div className="text-2xl font-bold">{sendResult.successful}</div>
                <div className="text-sm text-muted-foreground">Sent</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="text-center pt-6">
                <div className="text-2xl font-bold text-destructive">{sendResult.failed}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="text-center pt-6">
                <div className="text-2xl font-bold text-primary">{successRate.toFixed(0)}%</div>
                <div className="text-sm text-muted-foreground">Success Rate</div>
              </CardContent>
            </Card>
          </div>

          {/* Errors */}
          {sendResult.errors.length > 0 && (
            <div className="mb-6 space-y-2">
              <h4 className="text-sm font-medium">Errors</h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {sendResult.errors.map((error, idx) => (
                  <Alert key={idx} variant="destructive">
                    <AlertDescription className="text-sm">{error}</AlertDescription>
                  </Alert>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <Button onClick={reset} className="w-full">
            Send Another Campaign
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <UpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        limitType="sms"
        currentPlan={limits?.plan || 'starter'}
        currentUsage={usage?.current_month_sms_sent || 0}
        maxLimit={limits?.max_sms_monthly || 50}
      />
    </>
  )
}

export default SMSCampaign
